import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Form, Container, Button, Toast, Spinner } from "react-bootstrap";
import { axiosPost } from "../../../UTILS/AXIOS";
import { patientApi } from "../../../CONSTANTS";
import styles from "./PatientContactForm.module.css";
import FormSecurity from "../../FORM_SECURITY";
import {
  rules,
  securityFields,
  isSuccess,
  failureMessage,
  applyServerErrors,
} from "../../../UTILS/VALIDATION";

const schema = Yup.object().shape({
  fullName: rules.name("Full Name"),
  Address: rules.text("Preferred contact location", 200),
  city: rules.text("City", 100),
  state: rules.text("State", 100),
  phone: rules.phone(),
  email: rules.email(false),
  consent: Yup.boolean().oneOf([true], "Please give consent so a patient advocate can contact you."),
  ...securityFields,
});

const formFields = [
  { name: "fullName", label: "Full Name", type: "text", required: true, autoComplete: "name" },
  { name: "Address", label: "Address", type: "text", required: true, autoComplete: "street-address" },
  { name: "city", label: "City", type: "text", required: true, autoComplete: "address-level2" },
  { name: "state", label: "State", type: "text", required: true, autoComplete: "address-level1" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, autoComplete: "tel" },
  { name: "email", label: "Email Address", type: "email", required: false, autoComplete: "email" },
];

const PatientContactForm = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [loading, setLoading] = useState(false);
  const captcha = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), mode: "onTouched" });

  const onSubmit = async (data) => {
    setLoading(true);
    const result = await axiosPost(patientApi, {
      ...data,
      consent: data.consent ? "Yes" : "No",
      formType: "patientContact",
    });
    if (isSuccess(result)) {
      setToastMessage("Thank you. A patient advocate will reach out to you.");
      setToastVariant("success");
      reset();
    } else {
      applyServerErrors(result, setError, Object.keys(schema.fields));
      setToastMessage(failureMessage(result));
      setToastVariant("danger");
    }
    captcha.current?.reset();
    setShowToast(true);
    setLoading(false);
  };

  const renderField = ({ name, label, type, required, autoComplete }) => (
    <Form.Group controlId={`form${name}`} key={name}>
      <Form.Label className={styles.label}>
        {label}
        {required && <span className={styles.contactFormTextDanger}> *</span>}
      </Form.Label>
      <Form.Control
        type={type}
        {...register(name)}
        autoComplete={autoComplete}
        aria-required={required}
        isInvalid={!!errors[name]}
        disabled={loading}
        className={styles.contactFormFormControl}
      />
      <Form.Control.Feedback type="invalid">{errors[name]?.message}</Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Container className={`${styles.contactForm} position-relative`}>
      {loading && (
        <div className={styles.overlay}>
          <Spinner animation="border" variant="light" />
        </div>
      )}

      <Form noValidate onSubmit={handleSubmit(onSubmit)} className={styles.contactFormFormSection}>
        {formFields.map(renderField)}

        <Form.Group controlId="formConsent" className="mt-3">
          <div className={styles.contactFormCheckboxWrapper}>
            <Form.Check
              type="checkbox"
              label={
                <div className={styles.affirm}>
                  <span>
                    By clicking this, I consent to share the details above with the appropriate patient advocates.
                    <br />
                    The company affirms that your information will not be disclosed to any third party without
                    additional consent.
                  </span>
                </div>
              }
              {...register("consent")}
              isInvalid={!!errors.consent}
              feedback={errors.consent?.message}
              feedbackType="invalid"
              disabled={loading}
              className={styles.chkbox}
            />
          </div>
        </Form.Group>

        <FormSecurity ref={captcha} register={register} setValue={setValue} error={errors.recaptchaToken} theme="dark" />

        <Form.Group>
          <div className="d-flex justify-content-center mt-4">
            <Button type="submit" disabled={loading} className={styles.contactFormFormSubmit}>
              Submit
            </Button>
          </div>
        </Form.Group>
      </Form>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={6000}
        autohide
        role="status"
        className={`${styles.toastCustom} bg-${toastVariant} text-white position-fixed`}
        style={{ bottom: "10px", right: "10px", zIndex: 10000 }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default PatientContactForm;
